/* eslint-disable eqeqeq */
import React, { Fragment } from "react";
import PropTypes from "prop-types";
import { withStyles } from "@material-ui/styles";
import {
    Grid,
    TextField,
    Typography,
    Button,
    Divider,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    InputAdornment,
    Chip,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import DeleteIcon from "@material-ui/icons/Delete";
import { HttpRequest } from "../../../utils/httpRequests";
import NavBar from "../../../components/NavBar/NavBar";
import CustomizedSnackbar from "../../../components/CustomizedSnackbar/CustomizedSnackbar";
import LoginMenu from "../../../components/LoginMenu/LoginMenu";

const useStyles = () => ({
    root: {
        padding: 10,
        margin: "70px 20px",
    },
    topMargin: {
        marginTop: 20,
    },
    button: {
        textTransform: "capitalize",
        marginLeft: 10,
        padding: 10,
        width: 130,
    },
    searchButton: {
        textTransform: "capitalize",
        padding: 10,
        width: 110,
    },
    tableHeader: {
        backgroundColor: "#1976d2",
    },
    tableHeaderCell: {
        color: "#ffffff",
        fontWeight: 700,
    },
    sectionTitle: {
        fontWeight: 700,
        marginTop: 30,
        marginBottom: 10,
    },
    noData: {
        textAlign: "center",
        padding: 20,
        color: "#9e9e9e",
    },
});

class F354DashboardExcludedUsersForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            // ── Search ───────────────────────────────────────────────
            searchQuery: "",
            searchQueryError: "",
            searchResultsData: [],
            isSearchLoading: false,

            // ── Multi-select ─────────────────────────────────────────
            selectedUsers: [],   // array of selected user objects
            reason: "",

            // ── Add loading ──────────────────────────────────────────
            isAddLoading: false,

            // ── Excluded list ────────────────────────────────────────
            excludedUsersData: [],
            isExcludedListLoading: false,

            // ── Common ───────────────────────────────────────────────
            isOpenSnackbar: false,
            snackbarMessage: "",
            snackbarSeverity: "",
            isLoginMenu: false,
            isReload: true,
        };
    }

    componentDidMount() {
        this.loadExcludedUsers();
    }

    // ── Snackbar ──────────────────────────────────────────────────────────────
    handleSnackbar = (isOpen, message, severity) => {
        this.setState({ isOpenSnackbar: isOpen, snackbarMessage: message, snackbarSeverity: severity });
    };

    // ── Handle input change ───────────────────────────────────────────────────
    handleChange = (e) => {
        const { name, value } = e.target;
        this.setState({ [name]: value || "" });
    };

    // ── Strip _3084_DELETED style suffix from mobile/email ────────────────────
    cleanValue = (val) => {
        if (!val) return "—";
        return val.replace(/_\d+_DELETED$/i, "").trim();
    };

    // ── Search users ──────────────────────────────────────────────────────────
    searchUsers = async () => {
        const { searchQuery } = this.state;
        if (!searchQuery || searchQuery.trim() === "") {
            this.setState({ searchQueryError: "Please enter name, mobile or email to search." });
            return;
        }
        this.setState({ isSearchLoading: true, searchResultsData: [], searchQueryError: "" });

        const res = await HttpRequest(
            "GET",
            `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C20DashboardExcludedUsersSearch?query=${encodeURIComponent(searchQuery.trim())}`,
            null,
            "authorizeJson"
        );

        if (res.isSuccess) {
            this.setState({ searchResultsData: res.data || [] });
            if ((res.data || []).length === 0) {
                this.handleSnackbar(true, "No users found matching your search.", "info");
            }
        } else {
            if (res.statusCode === 401) { this.setState({ isLoginMenu: true }); }
            else { this.handleSnackbar(true, res.message || "Search failed.", "error"); }
        }
        this.setState({ isSearchLoading: false });
    };

    // ── Toggle user selection (select / deselect) ─────────────────────────────
    selectUser = (user) => {
        const { selectedUsers } = this.state;
        const alreadySelected = selectedUsers.find(u => u.id == user.id);
        if (alreadySelected) {
            // deselect
            this.setState({ selectedUsers: selectedUsers.filter(u => u.id != user.id) });
        } else {
            // select
            this.setState({ selectedUsers: [...selectedUsers, user] });
        }
    };

    // ── Remove one chip from selected list ────────────────────────────────────
    removeSelectedUser = (userId) => {
        this.setState({ selectedUsers: this.state.selectedUsers.filter(u => u.id != userId) });
    };

    // ── Clear all selections ──────────────────────────────────────────────────
    clearSelectedUser = () => {
        this.setState({ selectedUsers: [], reason: "" });
    };

    // ── Validate — reason is optional, just need at least 1 user ─────────────
    isAddFormValid = () => {
        if (this.state.selectedUsers.length === 0) {
            this.handleSnackbar(true, "Please select at least one user.", "warning");
            return false;
        }
        return true;
    };

    // ── Add all selected users to exclusion list ──────────────────────────────
    addExcludedUser = async () => {
        if (!this.isAddFormValid()) return;
        this.setState({ isAddLoading: true });

        const { selectedUsers, reason } = this.state;
        let successCount = 0;
        let failCount = 0;

        for (const user of selectedUsers) {
            const cleanMobile = this.cleanValue(user.mobileNo) === "—" ? "" : this.cleanValue(user.mobileNo);
            const cleanEmail = this.cleanValue(user.email) === "—" ? "" : this.cleanValue(user.email);

            const data = new FormData();
            data.append("userId", user.id);
            data.append("mobileNo", cleanMobile);
            data.append("email", cleanEmail);
            data.append("reason", reason || "");

            const res = await HttpRequest(
                "POST",
                `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C20DashboardExcludedUsersSave`,
                data,
                "authorize"
            );
            if (res.isSuccess) { successCount++; } else { failCount++; }
        }

        if (successCount > 0) {
            this.handleSnackbar(true, `${successCount} user(s) added to exclusion list successfully.`, "success");
            this.clearSelectedUser();
            this.setState({ searchQuery: "", searchResultsData: [] });
            this.loadExcludedUsers();
        }
        if (failCount > 0) {
            this.handleSnackbar(true, `${failCount} user(s) failed to add.`, "error");
        }
        this.setState({ isAddLoading: false });
    };

    // ── Load current exclusion list ───────────────────────────────────────────
    loadExcludedUsers = async () => {
        this.setState({ isExcludedListLoading: true });
        const res = await HttpRequest(
            "GET",
            `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C20DashboardExcludedUsersView`,
            null,
            "authorizeJson"
        );
        if (res.isSuccess) {
            this.setState({ excludedUsersData: res.data || [] });
        } else {
            if (res.statusCode === 401) { this.setState({ isLoginMenu: true }); }
            else { this.handleSnackbar(true, res.message || "Failed to load excluded users.", "error"); }
        }
        this.setState({ isExcludedListLoading: false });
    };

    // ── Remove user from exclusion list ──────────────────────────────────────
    removeExcludedUser = async (excludedId) => {
        const data = new FormData();
        data.append("excludedId", excludedId);
        const res = await HttpRequest(
            "POST",
            `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_SUB_API_NAME}/common/C20DashboardExcludedUsersDelete`,
            data,
            "authorize"
        );
        if (res.isSuccess) {
            this.handleSnackbar(true, "User removed from exclusion list.", "success");
            this.loadExcludedUsers();
        } else {
            if (res.statusCode === 401) { this.setState({ isLoginMenu: true }); }
            else { this.handleSnackbar(true, res.message || "Failed to remove user.", "error"); }
        }
    };

    // ── Search on Enter key ───────────────────────────────────────────────────
    handleSearchKeyDown = (e) => {
        if (e.key === "Enter") { this.searchUsers(); }
    };

    render() {
        const { classes } = this.props;
        const {
            searchQuery, searchQueryError, searchResultsData, isSearchLoading,
            selectedUsers, reason, isAddLoading,
            excludedUsersData, isExcludedListLoading,
        } = this.state;

        return (
            <Fragment>
                <LoginMenu
                    reload={this.state.isReload}
                    open={this.state.isLoginMenu}
                    handleClose={() => this.setState({ isLoginMenu: false })}
                />
                <CustomizedSnackbar
                    isOpen={this.state.isOpenSnackbar}
                    message={this.state.snackbarMessage}
                    severity={this.state.snackbarSeverity}
                    handleCloseSnackbar={() => this.handleSnackbar(false, "", "")}
                    autoHideDuration={3000}
                />
                <NavBar isAuthorize />

                <div className={classes.root}>

                    {/* ── Page Title ───────────────────────────────────────────────── */}
                    <Grid item xs={12}>
                        <Typography variant="h5" color="primary" style={{ fontWeight: 700 }}>
                            Dashboard Excluded Users
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                            Users added here will be excluded from the Orders Overview Dashboard.
                            Exclusion applies to all accounts sharing the same mobile number or email.
                        </Typography>
                    </Grid>
                    <Divider style={{ marginTop: 10, marginBottom: 10 }} />

                    {/* ── SECTION 1: Search ────────────────────────────────────────── */}
                    <div className={classes.topMargin}>
                        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
                            Search User
                        </Typography>
                        <Grid container spacing={2} alignItems="flex-start">

                            {/* Search input */}
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    autoComplete="off"
                                    id="searchQuery"
                                    name="searchQuery"
                                    label="Search by Name, Mobile No or Email"
                                    variant="outlined"
                                    fullWidth
                                    value={searchQuery}
                                    onChange={this.handleChange}
                                    onKeyDown={this.handleSearchKeyDown}
                                    error={!!searchQueryError}
                                    helperText={searchQueryError}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <SearchIcon color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>

                            {/* Search button */}
                            <Grid item xs={12} sm={4}>
                                <Button
                                    className={classes.searchButton}
                                    color="primary"
                                    variant="contained"
                                    onClick={this.searchUsers}
                                    disabled={isSearchLoading}
                                    style={{ height: 56 }}
                                >
                                    {isSearchLoading
                                        ? <CircularProgress size={23} style={{ color: "white" }} />
                                        : "Search"
                                    }
                                </Button>
                            </Grid>

                            {/* ── Search results table ── */}
                            {searchResultsData.length > 0 && (
                                <Grid item xs={12}>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow className={classes.tableHeader}>
                                                    <TableCell className={classes.tableHeaderCell}>#</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>User ID</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>Name</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>Mobile No</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>Email</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>Active</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>Delete</TableCell>
                                                    <TableCell className={classes.tableHeaderCell}>Action</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {searchResultsData.map((user, index) => {
                                                    const isSelected = !!selectedUsers.find(u => u.id == user.id);
                                                    return (
                                                        <TableRow key={user.id} hover selected={isSelected}>
                                                            <TableCell>{index + 1}</TableCell>
                                                            <TableCell>{(user.id)}</TableCell>

                                                            <TableCell>{user.fullName || user.displayName || "—"}</TableCell>
                                                            <TableCell>{this.cleanValue(user.mobileNo)}</TableCell>
                                                            <TableCell>{this.cleanValue(user.email)}</TableCell>
                                                            <TableCell>{(user.isActive)}</TableCell>
                                                            <TableCell>{(user.isDeleted)}</TableCell>

                                                            {/* ── Select / Deselect button per row ── */}
                                                            <TableCell>
                                                                <Button
                                                                    size="small"
                                                                    variant={isSelected ? "contained" : "outlined"}
                                                                    color="primary"
                                                                    style={{ textTransform: "capitalize" }}
                                                                    onClick={() => this.selectUser(user)}
                                                                >
                                                                    {isSelected ? "✓ Selected" : "Select"}
                                                                </Button>
                                                            </TableCell>

                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Grid>
                            )}
                        </Grid>
                    </div>

                    <Divider style={{ marginTop: 30, marginBottom: 10 }} />

                    {/* ── SECTION 2: Add to Exclusion List ─────────────────────────── */}
                    <div className={classes.topMargin}>
                        <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
                            Add to Exclusion List
                        </Typography>

                        {/* ── Selected users chips — shown when at least 1 is selected ── */}
                        {selectedUsers.length > 0 ? (
                            <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                <Grid item xs={12}>
                                    <Paper variant="outlined" style={{ padding: "12px 16px", backgroundColor: "#f5f5f5" }}>

                                        <Typography variant="body2" color="textSecondary" gutterBottom>
                                            {selectedUsers.length} user(s) selected
                                        </Typography>

                                        {/* One chip per selected user — click × to deselect */}
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                            {selectedUsers.map(user => (
                                                <Chip
                                                    key={user.id}
                                                    label={`${user.fullName || user.displayName || "—"}  •  ${this.cleanValue(user.mobileNo)}`}
                                                    color="primary"
                                                    size="small"
                                                    onDelete={() => this.removeSelectedUser(user.id)}  // ← × removes just this one
                                                />
                                            ))}
                                        </div>

                                        {/* Clear all button */}
                                        <Button
                                            size="small"
                                            color="secondary"
                                            style={{ textTransform: "capitalize", marginTop: 8 }}
                                            onClick={this.clearSelectedUser}
                                        >
                                            Clear All
                                        </Button>

                                    </Paper>
                                </Grid>
                            </Grid>
                        ) : (
                            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                                No users selected. Search and select one or more users above.
                            </Typography>
                        )}

                        <Grid container spacing={2}>

                            {/* ── Reason — optional ── */}
                            <Grid item xs={12} sm={8}>
                                <TextField
                                    autoComplete="off"
                                    id="reason"
                                    name="reason"
                                    label="Reason (optional — e.g. Internal team, Test account)"
                                    variant="outlined"
                                    fullWidth
                                    value={reason}
                                    onChange={this.handleChange}
                                    disabled={selectedUsers.length === 0}   // ← disabled until a user is selected
                                />
                            </Grid>

                            {/* ── Add to List button ── */}
                            <Grid item xs={12} sm={4}>
                                <Button
                                    className={classes.searchButton}
                                    color="primary"
                                    variant="contained"
                                    onClick={this.addExcludedUser}
                                    disabled={isAddLoading || selectedUsers.length === 0}
                                    style={{ height: 56 }}
                                >
                                    {isAddLoading
                                        ? <CircularProgress size={23} style={{ color: "white" }} />
                                        : "Add To List"
                                    }
                                </Button>
                            </Grid>

                        </Grid>
                    </div>

                    <Divider style={{ marginTop: 30, marginBottom: 10 }} />

                    {/* ── SECTION 3: Current Exclusion List ───────────────────────── */}
                    <div className={classes.topMargin}>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item>
                                <Typography variant="subtitle1" color="primary" className={classes.sectionTitle}>
                                    Current Exclusion List
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Button
                                    size="small"
                                    color="primary"
                                    variant="outlined"
                                    style={{ textTransform: "capitalize" }}
                                    onClick={this.loadExcludedUsers}
                                    disabled={isExcludedListLoading}
                                >
                                    {isExcludedListLoading ? <CircularProgress size={16} /> : "Refresh"}
                                </Button>
                            </Grid>
                        </Grid>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow className={classes.tableHeader}>
                                        <TableCell className={classes.tableHeaderCell}>#</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>User ID</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Mobile No</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Email</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Reason</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Added On</TableCell>
                                        <TableCell className={classes.tableHeaderCell}>Remove</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {isExcludedListLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className={classes.noData}>
                                                <CircularProgress size={24} />
                                            </TableCell>
                                        </TableRow>
                                    ) : excludedUsersData.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className={classes.noData}>
                                                No users excluded yet.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        excludedUsersData.map((row, index) => (
                                            <TableRow key={row.id} hover>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{row.userId || "—"}</TableCell>
                                                <TableCell>{row.mobileNo || "—"}</TableCell>
                                                <TableCell>{row.email || "—"}</TableCell>
                                                <TableCell>{row.reason || "—"}</TableCell>
                                                <TableCell>{row.addedOn || "—"}</TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        size="small"
                                                        color="secondary"
                                                        onClick={() => this.removeExcludedUser(row.id)}
                                                        title="Remove from exclusion list"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </div>

                </div>
            </Fragment>
        );
    }
}

F354DashboardExcludedUsersForm.propTypes = {
    classes: PropTypes.object.isRequired,
};

export default withStyles(useStyles)(F354DashboardExcludedUsersForm);